// loader.js - Easy HTTP request functions for Shopify themes

class Loader {
  constructor(options = {}) {
    this.baseURL = options.baseURL || '';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...options.defaultHeaders
    };
    this.timeout = options.timeout || 10000;
  }

  // Base request method
  async request(url, options = {}) {
    const config = {
      method: 'GET',
      headers: { ...this.defaultHeaders, ...options.headers },
      ...options
    };

    // Add timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    config.signal = controller.signal;

    try {
      const response = await fetch(this.baseURL + url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Try to parse JSON, fallback to text
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  // GET request
  async get(url, params = {}) {
    const urlParams = new URLSearchParams(params);
    const queryString = urlParams.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    
    return this.request(fullUrl);
  }

  // POST request
  async post(url, data = {}, options = {}) {
    return this.request(url, {
      method: 'POST',
      body: this.prepareBody(data),
      ...options
    });
  }

  // PUT request
  async put(url, data = {}, options = {}) {
    return this.request(url, {
      method: 'PUT',
      body: this.prepareBody(data),
      ...options
    });
  }

  // PATCH request
  async patch(url, data = {}, options = {}) {
    return this.request(url, {
      method: 'PATCH',
      body: this.prepareBody(data),
      ...options
    });
  }

  // DELETE request
  async delete(url, options = {}) {
    return this.request(url, {
      method: 'DELETE',
      ...options
    });
  }

  // Helper to prepare request body
  prepareBody(data) {
    if (data instanceof FormData) {
      return data;
    }
    if (typeof data === 'object') {
      return JSON.stringify(data);
    }
    return data;
  }

  // Set default headers
  setDefaultHeaders(headers) {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  // Set base URL
  setBaseURL(baseURL) {
    this.baseURL = baseURL;
  }
}

// Create default instance
const loader = new Loader();

// Shopify-specific helper functions
const shopifyLoader = {
  // Add to cart
  async addToCart(items) {
    return loader.post('/cart/add.js', { items });
  },

  // Update cart
  async updateCart(updates) {
    return loader.post('/cart/update.js', { updates });
  },

  // Get cart
  async getCart() {
    return loader.get('/cart.js');
  },

  // Clear cart
  async clearCart() {
    return loader.post('/cart/clear.js');
  },

  // Get product data
  async getProduct(handle) {
    return loader.get(`/products/${handle}.js`);
  },
};

window.loader = loader;
window.Loader = Loader;
window.shopifyLoader = shopifyLoader;
